import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, Typography } from '@mui/material';

import type {
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent
} from 'react';

import type { SpotHistoryTableColumnType } from '@/types/markets';

import { COLUMN_GAP } from './constants';
import HeaderExpandedDetails from './HeaderExpandedDetails';
import ResizeHandle from './ResizeHandle';
import { getColumnCode, getColumnTitle } from './utils';

type Props = {
  column: SpotHistoryTableColumnType;
  columnIndex: number;
  columnWidth: number;
  rowHeight: number;
  isHeaderExpanded: boolean;
  virtualStart: number;
  virtualSize: number;
  onToggle: () => void;
  onToggleKeyDown: (event: KeyboardEvent) => void;
  onResizePointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onResizeKeyDown: (event: KeyboardEvent) => void;
  onResizeDoubleClick: (event: ReactMouseEvent) => void;
};

const ColumnHeaderCell = ({
  column,
  columnIndex,
  columnWidth,
  rowHeight,
  isHeaderExpanded,
  virtualStart,
  virtualSize,
  onToggle,
  onToggleKeyDown,
  onResizePointerDown,
  onResizeKeyDown,
  onResizeDoubleClick
}: Props) => {
  const columnCode = getColumnCode(column);
  const title = getColumnTitle(column);

  return (
    <Box
      aria-colindex={columnIndex + 1}
      role='columnheader'
      aria-expanded={isHeaderExpanded}
      data-resize-col={columnCode}
      data-resize-col-header='true'
      data-resize-col-order={columnIndex}
      tabIndex={0}
      style={{
        left: virtualStart,
        height: rowHeight || undefined
      }}
      sx={theme => ({
        backgroundColor: 'gx.tileDark',
        borderBottom: `0.5px solid ${theme.palette.gx.text}`,
        cursor: 'pointer',
        flex: 1,
        px: 0.5,
        py: 1,
        position: 'absolute',
        top: 0,
        width: virtualSize - COLUMN_GAP,
        '&:focus-visible': {
          outline: `1px solid ${theme.palette.gx.highlight}`,
          outlineOffset: -2
        }
      })}
      onClick={onToggle}
      onKeyDown={onToggleKeyDown}
    >
      <Box
        data-header-cell-content
        sx={{
          overflow: 'hidden',
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            flex: 1,
            gap: 1,
            minWidth: 0
          }}
        >
          <Typography
            sx={{
              display: '-webkit-box',
              flex: 1,
              fontSize: 14,
              lineHeight: 1.25,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }}
          >
            {title}
          </Typography>
          <KeyboardArrowDown
            aria-hidden='true'
            sx={{
              color: 'gx.text',
              flexShrink: 0,
              fontSize: 18,
              transform: isHeaderExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease'
            }}
          />
        </Box>
        {isHeaderExpanded && <HeaderExpandedDetails column={column} />}
      </Box>
      <ResizeHandle
        columnWidth={columnWidth}
        label={title}
        onPointerDown={onResizePointerDown}
        onKeyDown={onResizeKeyDown}
        onDoubleClick={onResizeDoubleClick}
      />
    </Box>
  );
};

export default ColumnHeaderCell;
